import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useIntl } from '@edx/frontend-platform/i18n';
import {
  Form, Icon, IconButton, useToggle,
} from '@edx/paragon';
import PropTypes from 'prop-types';

import messages from './messages';
import { 
  EyeIcon,
  EyeClosedIcon  
} from "@phosphor-icons/react";

import { validatePasswordField, validateConfirmPasswordField } from '../data/utils/passwordValidator';
import { clearRegistrationBackendError, fetchRealtimeValidations } from '../register/data/actions';

import { GreenSuccessCircleSVGcampusNew, RedErrorCircleSVGcampusNew } from '@edx/brand'

const PasswordField = (props) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const validationApiRateLimited = useSelector(state => state.register.validationApiRateLimited);
  const [isPasswordHidden, setHiddenTrue, setHiddenFalse] = useToggle(true);

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === props.name && e.relatedTarget?.name === 'passwordIcon') {
      return; // Do not run validations on password icon click
    }

    let passwordValue = value;
    if (name === 'passwordIcon') {
      // To validate actual password value when onBlur is triggered by focusing out the password icon
      passwordValue = props.value;
    }

    if (props.handleBlur) {
      props.handleBlur({
        target: {
          name: props.name,
          value: passwordValue,
        },
      });
    }

    if (props.handleErrorChange) {
      let fieldError = '';
      
      if (props.name === 'password') {
        fieldError = validatePasswordField(passwordValue, formatMessage);
      } else if (props.name === 'confirm_password') {
        fieldError = validateConfirmPasswordField(passwordValue, props.mainPasswordValue || '', formatMessage);
      }
      
      if (fieldError) {
        props.handleErrorChange(props.name, fieldError);
      } else if (!validationApiRateLimited && props.name === 'password') {
        dispatch(fetchRealtimeValidations({ password: passwordValue }));
      }
    }
  };

  const handleFocus = (e) => {
    if (e.target?.name === 'passwordIcon') {
      return; // Do not clear error on password icon focus
    }

    if (props.handleFocus) {
      props.handleFocus(e);
    }
    if (props.handleErrorChange) {
      props.handleErrorChange(props.name, '');
      dispatch(clearRegistrationBackendError(props.name));
    }
  };

  const HideButton = (
    <IconButton
      onFocus={handleFocus}
      onBlur={handleBlur}
      name="passwordIcon"
      iconAs={EyeClosedIcon}
      onClick={setHiddenTrue}
      size="sm"
      alt={formatMessage(messages['hide.password'])}
    />
  );

  const ShowButton = (
    <IconButton
      onFocus={handleFocus}
      onBlur={handleBlur}
      name="passwordIcon"
      iconAs={EyeIcon}
      onClick={setHiddenFalse}
      size="sm"
      alt={formatMessage(messages['show.password'])}
      tabIndex={-1}
    />
  );

  const hasError = Boolean(props.errorMessage);

  return (
    <Form.Group
      controlId={props.name}
      className={`position-relative form-field ${
        hasError ? 'has-error' : ''
      } form-type ${props.className}`}
      isInvalid={props.errorMessage !== ''}
    >
      {(props.floatingLabel || props.alwaysShowLabel) && (
        <Form.Label
          className={`form-label regular-text ml-3 mb-0 ${props.borderClass} ${
            props.value ? 'visible' : 'hidden'
          }`}
        >
          {props.floatingLabel}
        </Form.Label>
      )}
      {(props.absoluteLabel || props.alwaysShowLabel) && (
        <Form.Label
          className={`form-fields-label ${props.borderClass} ${
            props.value ? 'visible' : 'hidden'
          }`}
        >
          {props.absoluteLabel}
        </Form.Label>
      )}
      <Form.Control
        as="input"
        className={`form-group__form-field ${props.inputClassName || ''}`}
        type={isPasswordHidden ? 'password' : 'text'}
        name={props.name}
        value={props.value}
        autoComplete={props.autoComplete}
        aria-invalid={props.errorMessage !== ''}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={props.handleChange}
        leadingElement={
          // eslint-disable-next-line no-nested-ternary
          props.showStatusIcon ? (
            props.passwordStatusSuccess ? (
              <img src={GreenSuccessCircleSVGcampusNew} alt="" />
            ) : (
              <img src={RedErrorCircleSVGcampusNew} alt="" />
            )
          ) : (
            ''
          )
        }
        // eslint-disable-next-line no-nested-ternary
        trailingElement={
          props.value ? (isPasswordHidden ? ShowButton : HideButton) : ''
        }
        placeholder={props.placeholder}
      />
      {props.showErrorMessage && (
        <Form.Control.Feedback
          key="error"
          className={`form-text-size ${props.errorMessage ? 'form-text-invalid' : ''}`}
          hasIcon={false}
          feedback-for={props.name}
          type="invalid"
        >
          <p>{props.errorMessage || '\u00A0'}</p>
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

PasswordField.defaultProps = {
  inputClassName: '',
  borderClass: '',
  absoluteLabel: '',
  floatingLabel: '',
  errorMessage: '',
  handleBlur: null,
  handleFocus: null,
  alwaysShowLabel: false,
  showStatusIcon: false,
  passwordStatusSuccess: false,
  handleChange: () => {},
  placeholder: '',
  handleErrorChange: null,
  showErrorMessage: false,
  autoComplete: null,
  mainPasswordValue: '',
};

PasswordField.propTypes = {
  borderClass: PropTypes.string,
  absoluteLabel: PropTypes.string,
  errorMessage: PropTypes.string,
  floatingLabel: PropTypes.string,
  handleBlur: PropTypes.func,
  handleFocus: PropTypes.func,
  alwaysShowLabel: PropTypes.bool,
  handleChange: PropTypes.func,
  showStatusIcon: PropTypes.bool,
  passwordStatusSuccess: PropTypes.bool,
  placeholder: PropTypes.string,
  handleErrorChange: PropTypes.func,
  showErrorMessage: PropTypes.bool,
  name: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  autoComplete: PropTypes.string,
  inputClassName: PropTypes.string,
  mainPasswordValue: PropTypes.string,
};

export default PasswordField;
